import type {
  Asset,
  AssetDistribution,
  AssetGroupCount,
  AssetStackGroup,
  AssetType,
  ColorConfig,
  CustomField
} from '~/types/asset'
import type { EndpointsRegistry, FetchClient } from '~/api/types'
import type { ActionCapability, AssetCapabilities, AssetCapability, AssetsSTC } from '~/types/capabilities'
import type { ActionPayload, DynamicPayload } from '~/types/actions'
import { GatewayEndpoints } from '~/api/endpoints/gateway-endpoints'
import type { GenericObject, SuccessNoContent } from '~/types/common'
import type { AssetsService } from '~/api/services/types'
import type { AssetFilters } from '~/types/filters'
import type { AssetConfiguration, ConfigurationValues } from '~/types/asset-configuration'
import type { AssetPresetSyncOptions, GroupPresetSyncResult } from '~/types/configurationPreset'
import type { PaginatedResult } from '~/types/pagination'
import type {
  EffectiveAlertLevelColor,
  MapFeaturesResponse,
  MapIconConfig,
  NetworkStatusResponse
} from '~/types/map-assets.types'
import { buildAssetConfigurations } from '~/helpers/asset-configurations'
import { isAssetCapabilitiesMocked, isGroupPresetMocked } from '~/composables/useFeatureFlags'

// TODO: drop with mocks/assetCapabilitiesMock.ts once the panel capabilities exist locally
export const IS_ASSET_CAPABILITIES_MOCKED = isAssetCapabilitiesMocked

interface AssetPresetSyncResponse {
  sent?: number
  skipped?: number
  invalid_action_keys?: string[]
}

export const registerEndpoints = (client: FetchClient, registry: EndpointsRegistry): AssetsService => {
  return {
    getMapIconColors: async () => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GET_MAP_ICON_COLORS)
      return await client<ColorConfig[]>(url, { method, headers, body })
    },

    getAlertLevelColors: async () => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GET_ALERT_LEVEL_COLORS)
      return await client<EffectiveAlertLevelColor[]>(url, { method, headers, body })
    },

    getMapIconConfigs: async () => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GET_MAP_ICON_CONFIGS)
      return await client<MapIconConfig[]>(url, { method, headers, body })
    },

    getAssetTypes: async () => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.ASSET_TYPES_GET_ALL)
      return await client<AssetType[]>(url, { method, headers, body })
    },

    getFilteredAssets: async (filters: AssetFilters) => {
      const { page, pageSize, ...bodyFilters } = filters

      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_GET_FILTERED,
        {
          queryParams: {
            current_page: page || 1,
            per_page: pageSize || 10
          }
        },
        bodyFilters
      )

      const response = await client<PaginatedResult<Asset>>(url, { method, headers, body })

      // Hotfix plaval: Keep only essential states for LNLV components
      for (const asset of response.data) {
        for (const component of asset.components ?? []) {
          if (
            component.assetType?.id === 'lnlv' ||
            component.assetType?.id === 'street_parking_management_panel_lnlv'
          ) {
            const essentialStates = [
              'POWER_STATE',
              'REBOOT_CAUSE',
              'TEXT',
              'BATTERY_VOLTAGE',
              'BATTERY_IS_CHARGING',
              'BATTERY_LEVEL',
              'LOAD_CURRENT'
            ]
            component.lastStates = component.lastStates?.filter(
              (state) => state.stateKey.startsWith('LNLV_') || essentialStates.includes(state.stateKey)
            )
          }
        }
      }

      return response
    },

    getFilteredAssetIds: async (filters: AssetFilters) => {
      const { page, pageSize, ...bodyFilters } = filters
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_GET_FILTERED_IDS,
        {},
        bodyFilters
      )
      return await client<number[]>(url, { method, headers, body })
    },

    getFilteredAssetDistribution: async (filters: AssetFilters) => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_GET_FILTERED_DISTRIBUTION,
        {},
        filters
      )
      return await client<AssetDistribution>(url, { method, headers, body })
    },

    getAssetById: async (id: number) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.ASSETS_GET_BY_ID, {
        params: { id }
      })
      return await client<Asset>(url, { method, headers, body })
    },

    getGroupedAssetsCount: async (groupBy: AssetStackGroup) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GROUPED_ASSET_COUNT, {
        queryParams: { groupBy }
      })
      return await client<AssetGroupCount[]>(url, { method, headers, body })
    },

    getAssetsMapFeatures: async (originGeohash, exclude = []) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.ASSETS_GET_MAP_FEATURES, {
        queryParams: { origin: originGeohash, exclude }
      })
      return await client<MapFeaturesResponse>(url, { method, headers, body })
    },

    getAssetsNetworkStatuses: async (filters: AssetFilters) => {
      const { page, pageSize, ...bodyFilters } = filters
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_GET_NETWORK_STATUSES,
        {},
        bodyFilters
      )
      return await client<NetworkStatusResponse>(url, { method, headers, body })
    },

    createAsset: async (assetData: Partial<Asset>) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.ASSETS_CREATE, {}, assetData)
      return await client<Asset>(url, { method, headers, body })
    },

    updateAsset: async (id: number, assetData: Partial<Asset>) => {
      const assetDataWithoutSite = { ...assetData }
      delete assetDataWithoutSite.siteId
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_UPDATE,
        { params: { id } },
        assetDataWithoutSite
      )
      return await client<Asset>(url, { method, headers, body })
    },

    deleteAsset: async (id: number) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.ASSETS_DELETE, { params: { id } })
      return await client<GenericObject>(url, { method, headers, body })
    },

    getAssetsActionsByAssetIds: async (
      assetIds: number[],
      context = 'STANDARD',
      assetTypeIds: string[] = []
    ): Promise<AssetCapabilities> => {
      if (IS_ASSET_CAPABILITIES_MOCKED) {
        const { getMockAssetsActions } = await import('~/mocks/assetCapabilitiesMock')
        return await getMockAssetsActions()
      }

      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_ACTIONS_GET_BY_ASSET_IDS,
        {
          queryParams: { invoke_context: context }
        },
        {
          assetIds: assetIds,
          ...(assetTypeIds.length > 0 && { assetTypeIds })
        }
      )
      return await client<AssetCapabilities>(url, { method, headers, body })
    },

    assignGroupsToAsset: async (assetId: number, groupIds: number[]) => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_ASSIGN_GROUPS,
        { params: { id: assetId } },
        { groupIds }
      )
      return await client<Asset>(url, { method, headers, body })
    },

    updateAssetCustomField: async (assetId: number, customFieldId: string, value: string) => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_UPDATE_CUSTOM_FIELD,
        { params: { asset_id: assetId, custom_field_id: customFieldId } },
        { value }
      )
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    postAssetAction: async <C extends ActionCapability>(
      assetIds: number[],
      actionKey: string,
      payload: ActionPayload<C> | DynamicPayload<AssetCapabilities>
    ): Promise<SuccessNoContent> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_ACTIONS_POST,
        { params: { action_id: actionKey } },
        {
          assetIds: assetIds,
          payload
        }
      )
      return await client<SuccessNoContent>(url, { method, headers, body })
    },

    getAssetConfigurations: async (asset: Asset, capabilities: AssetCapability[]): Promise<AssetConfiguration[]> =>
      buildAssetConfigurations(asset, capabilities),

    readAssetConfiguration: async (assetId: number, readActionKey: string): Promise<AssetConfiguration | null> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_ACTIONS_POST,
        { params: { action_id: readActionKey } },
        { assetIds: [assetId], payload: {} }
      )
      await client<SuccessNoContent>(url, { method, headers, body })
      return null
    },

    syncAssetPreset: async (assetId: number, options: AssetPresetSyncOptions = {}): Promise<GroupPresetSyncResult> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_SYNC_PRESETS,
        { params: { id: assetId } },
        {
          ...(options.force && { force: true }),
          ...(options.actionKeys?.length && { action_keys: options.actionKeys })
        }
      )
      const data = await client<AssetPresetSyncResponse | null>(url, { method, headers, body })
      return {
        sent: data?.sent ?? 0,
        skipped: data?.skipped ?? 0,
        ...(data?.invalid_action_keys?.length && { invalidActionKeys: data.invalid_action_keys })
      }
    },

    putAssetConfiguration: async (
      assetId: number,
      actionKey: string,
      values: ConfigurationValues
    ): Promise<SuccessNoContent> => {
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.ASSETS_ACTIONS_POST,
        { params: { action_id: actionKey } },
        { assetIds: [assetId], payload: values }
      )
      const result = await client<SuccessNoContent>(url, { method, headers, body })
      if (isGroupPresetMocked) {
        const { simulateDeviceAnswer } = await import('~/mocks/groupPresetMock')
        simulateDeviceAnswer(assetId, values)
      }
      return result
    },

    getAssetTypeCustomFields: async (assetTypeId: string) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.GET_ASSET_TYPE_CUSTOM_FIELDS, {
        params: { asset_type_id: assetTypeId }
      })
      return await client<CustomField[]>(url, { method, headers, body })
    },

    refreshLastestLog: async (assetId: number) => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.REFRESH_LATEST_LOG, {
        params: { id: assetId }
      })
      return await client(url, { method, headers, body })
    },

    getAssetsSTC: async (): Promise<AssetsSTC> => {
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.STATES_AND_TELEMETRY)
      return await client<AssetsSTC>(url, { method, headers, body })
    }
  }
}
